// src/components/Trust.js

import React, { useState, useEffect } from 'react';
import { Award, Shield, Star, CheckCircle, ArrowRight } from 'lucide-react';

// Add font import
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);



const Trust = () => {
  const [visibleLogos, setVisibleLogos] = useState([]);
  const [hoveredLogo, setHoveredLogo] = useState(null);
  
  const companies = [
    { 
      id: 1, 
      name: 'Super Glue', 
      category: 'Przemysł', 
      employees: '500+', 
      funding: '5M PLN',
      logo: '/superglue.png'
    },
    { 
      id: 2, 
      name: 'Hydromarko', 
      category: 'Energia', 
      employees: '200+', 
      funding: '12M PLN',
      logo: '/hydromakro.png'
    },
    { 
      id: 3, 
      name: 'Marbet', 
      category: 'Produkcja', 
      employees: '150+', 
      funding: '8M PLN',
      logo: '/marbet.png'
    },
    { 
      id: 4, 
      name: 'Biovena', 
      category: 'Biotechnologia', 
      employees: '300+', 
      funding: '15M PLN',
      logo: '/biovena.png'
    },
    { 
      id: 5, 
      name: 'Aplast', 
      category: 'Przemysł', 
      employees: '100+', 
      funding: '3M PLN',
      logo: '/logo-aplast.png'
    },
    { 
      id: 6, 
      name: 'Fabryka Chusteczek', 
      category: 'Produkcja', 
      employees: '200+', 
      funding: '7M PLN',
      logo: '/logo-fabryka-chusteczek-top2.png'
    },
    /*{ id: 5, name: 'AgriTech Pro', category: 'Rolnictwo', employees: '100+', funding: '3M PLN', logo: null },
    { id: 6, name: 'EduTech Platform', category: 'Edukacja', employees: '250+', funding: '6M PLN', logo: null },
    { id: 7, name: 'LogiTech Systems', category: 'Logistyka', employees: '400+', funding: '10M PLN', logo: null },
    { id: 8, name: 'BioTech Labs', category: 'Biotechnologia', employees: '180+', funding: '20M PLN', logo: null } */
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleLogos(prev => {
        if (prev.length < companies.length) {
          return [...prev, companies[prev.length].id];
        }
        clearInterval(timer);
        return prev;
      });
    }, 150);

    return () => clearInterval(timer);
  }, []);

  return (
    <section id="o-nas" className="relative overflow-hidden bg-black py-14 md:py-16">
      <div className="absolute inset-0">
        <div className="absolute inset-0"></div>
        <div className="absolute top-10 left-10 animate-float opacity-10">
          <Shield className="w-20 h-20 text-blue-400" />
        </div>
        <div className="absolute bottom-10 right-10 animate-float animation-delay-2000 opacity-10">
          <Award className="w-24 h-24 text-purple-400" />
        </div>
        <div className="absolute top-1/2 left-20 animate-float animation-delay-1000 opacity-10">
          <Star className="w-16 h-16 text-indigo-400" />
        </div>
      </div>
      
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-violet-500"></div>
            <span className="text-sm font-medium text-gray-400 uppercase tracking-wider">Nasi klienci</span>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-violet-500"></div>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-black text-white mb-4 uppercase leading-tight" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 900 }}>
            Zaufali nam liderzy rynku
          </h2>
          
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Pomagamy firmom z różnych branż w pozyskiwaniu funduszy i rozwoju biznesu
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 lg:gap-6">
          {companies.map((company, index) => (
            <div
              key={company.id}
              className={`
                relative group cursor-pointer
                transform transition-all duration-500
                ${visibleLogos.includes(company.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
                ${hoveredLogo === company.id ? 'z-50' : 'z-10'}
              `}
              /* ======================================================
                 ZMIANA: Efekt hover został tymczasowo wyłączony.
                 Aby go przywrócić, usuń ten blok komentarza.
              
                 onMouseEnter={() => setHoveredLogo(company.id)}
                 onMouseLeave={() => setHoveredLogo(null)}
                 ====================================================== */
            >
              <div className={`
                relative rounded-xl md:rounded-2xl p-4 sm:p-6 h-full
                backdrop-blur-sm bg-white/5 border border-white/10
                transition-all duration-300
                ${hoveredLogo === company.id ? 'border-blue-400/50 bg-white/10 scale-105' : ''}
                hover:shadow-2xl hover:shadow-blue-500/10
              `}>
                <div className={`
                  absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-400/20 to-purple-400/20
                  opacity-0 group-hover:opacity-100 transition-opacity duration-300
                  blur-xl
                `}></div>
                
                <div className="relative z-10 flex flex-col items-center justify-center h-full">
                  {company.logo ? (
                    <img 
                      src={company.logo} 
                      alt={company.name}
                      className="h-10 sm:h-12 md:h-16 w-auto max-w-[120px] sm:max-w-[150px] object-contain mb-3 filter grayscale group-hover:grayscale-0 transition-all duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="50" viewBox="0 0 100 50"%3E%3Crect width="100" height="50" fill="%23374151" rx="8"/%3E%3C/svg%3E';
                      }}
                    />
                  ) : (
                    <div className="w-28 h-14 bg-gradient-to-br from-gray-600 to-gray-700 rounded-lg mb-3 group-hover:from-blue-500 group-hover:to-purple-500 transition-all duration-300"></div>
                  )}
                  
                  <div className={`
                    absolute -bottom-2 left-0 right-0 text-center
                    transition-all duration-300
                    ${hoveredLogo === company.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}
                  `}>
                    <p className="text-sm font-semibold text-white">{company.name}</p>
                  </div>
                </div>

                <div className={`
                  absolute ${index < 4 ? 'top-full mt-3' : 'bottom-full mb-3'}
                  left-1/2 -translate-x-1/2 w-48
                  bg-gray-900 rounded-xl p-3 shadow-2xl
                  transition-all duration-300 pointer-events-none z-50
                  ${hoveredLogo === company.id ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
                  lg:${index < 4 ? 'top-full mt-3' : 'bottom-full mb-3'}
                  ${index % 2 === 0 ? 'sm:left-3' : 'sm:right-3 sm:left-auto'}
                  sm:translate-x-0
                `}>
                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Branża:</span>
                      <span className="text-white">{company.category}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Pracownicy:</span>
                      <span className="text-white">{company.employees}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Dotacja:</span>
                      <span className="text-green-400">{company.funding}</span>
                    </div>
                  </div>
                  
                  <div className={`
                    absolute left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-900 rotate-45
                    ${index < 4 ? '-top-1.5' : '-bottom-1.5'}
                    lg:${index < 4 ? '-top-1.5' : '-bottom-1.5'}
                  `}></div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-400 mb-4">
            Dołącz do grona zadowolonych klientów
          </p>
{/*
        <button className="group relative inline-flex items-center gap-3 px-6 py-3 text-white hover:text-violet-500 font-semibold transition-colors">
          Zobacz case studies
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          <span className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent opacity-50 group-hover:opacity-100 transition-opacity"></span>
        </button>
*/}
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(10deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </section>
  );
};

export default Trust;